/**
 * @fileoverview Convert Pre-Appointment Dialog
 *
 * Purpose: Modal that converts a pre-appointment (Flow A) job into a
 * fully appointed job (Flow A → A9). Captures the appointment date,
 * type, optional notes, and an optional Notice of Appointment file. When
 * a NOA is attached it flows through the standard documents pipeline;
 * otherwise it can be uploaded later. On submit the job's status flips
 * to `day_one` (or `active` if the user picks that).
 *
 * Used by:
 * - Pre-appointment banner CTA on the job overview / plan page.
 * - Pre-appointment job settings page.
 */

import { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/composed/overlays/dialog";
import { Button } from "@/ui/primitives/button";
import { Label } from "@/ui/primitives/label";
import { Textarea } from "@/ui/primitives/textarea";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import { DatePicker } from "@/ui/composed/inputs/date-picker";
import { Check, FileText, Loader2, Upload, X } from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import { useConvexMutation, useConvexAction } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import type { AppointmentType } from "@cvx/jobs/schema";
import { toast } from "sonner";
import { cn } from "@/utils/misc";
import {
  CORPORATE_APPOINTMENT_TYPES,
  PERSONAL_APPOINTMENT_TYPES,
} from "@/lib/precedent-templates";

/**
 * Appointment type options. Mirrors the labels used in the create-job
 * dialog so the two flows feel consistent.
 */
const APPOINTMENT_TYPE_LABELS: Record<string, string> = {
  cvl: "Creditors Voluntary Liquidation (CVL)",
  mvl: "Members Voluntary Liquidation (MVL)",
  simplified_liquidation: "Simplified Liquidation",
  court_liquidation: "Court Liquidation",
  provisional_liquidation: "Provisional Liquidation",
  sbr: "Small Business Restructuring (SBR)",
  voluntary_administration: "Voluntary Administration (VA)",
  deed_of_company_arrangement: "Deed of Company Arrangement (DOCA)",
  receivership: "Receivership",
  scheme_of_arrangement: "Scheme of Arrangement",
  // MOR-1838 — a personal pre-appointment converts into a personal
  // appointment, so those types have to be offered too.
  creditors_petition: "Creditors Petition",
  debtors_petition: "Debtors Petition",
  part_x_arrangement: "Personal Insolvency Agreement (Part X)",
  personal_bankruptcy: "Personal Bankruptcy",
  business_bankruptcy: "Business Bankruptcy",
};

/** Picker groups, so corporate and personal types don't read as one list. */
const APPOINTMENT_TYPE_GROUPS = [
  { label: "Corporate", values: CORPORATE_APPOINTMENT_TYPES },
  { label: "Personal insolvency", values: PERSONAL_APPOINTMENT_TYPES },
] as const;

const ACCEPTED_FILE_TYPES = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/msword",
];

const MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024;

interface ConvertPreAppointmentDialogProps {
  jobId: Id<"jobs">;
  /**
   * Pre-filled appointment type captured at creation time. The user may
   * override it; we don't enforce that they keep the original value.
   */
  initialAppointmentType?: AppointmentType;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called once the conversion mutation resolves successfully. */
  onConverted?: () => void;
}

export function ConvertPreAppointmentDialog({
  jobId,
  initialAppointmentType,
  open,
  onOpenChange,
  onConverted,
}: ConvertPreAppointmentDialogProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [appointmentDate, setAppointmentDate] = useState<string>("");
  const [appointmentType, setAppointmentType] = useState<AppointmentType | "">(
    initialAppointmentType ?? "",
  );
  const [notes, setNotes] = useState<string>("");
  const [targetStatus, setTargetStatus] = useState<"day_one" | "active">(
    "day_one",
  );
  const [file, setFile] = useState<File | null>(null);
  const [storageId, setStorageId] = useState<Id<"_storage"> | null>(null);
  const [uploadPhase, setUploadPhase] = useState<
    "idle" | "uploading" | "uploaded" | "error"
  >("idle");
  const [uploadError, setUploadError] = useState<string>("");

  // Reset state when the dialog re-opens so a half-finished previous
  // attempt doesn't leak into the next one.
  useEffect(() => {
    if (!open) return;
    setAppointmentDate("");
    setAppointmentType(initialAppointmentType ?? "");
    setNotes("");
    setTargetStatus("day_one");
    setFile(null);
    setStorageId(null);
    setUploadPhase("idle");
    setUploadError("");
  }, [open, initialAppointmentType]);

  const generateUploadUrl = useConvexMutation(
    api.jobs.noticeOfAppointment.mutations.generateNoaUploadUrl,
  );
  const { mutateAsync: convertJob, isPending: isConverting } = useMutation({
    mutationFn: useConvexMutation(api.jobs.mutations.convertPreAppointmentJob),
  });
  const { mutateAsync: extractNoaFields } = useMutation({
    mutationFn: useConvexAction(
      api.jobs.noticeOfAppointment.actions.extractNoaFields,
    ),
  });
  const { mutateAsync: updateJob } = useMutation({
    mutationFn: useConvexMutation(api.jobs.mutations.updateJob),
  });

  const handleFileSelected = async (selected: File) => {
    if (!ACCEPTED_FILE_TYPES.includes(selected.type)) {
      setUploadError("Please upload a PDF or Word document.");
      setUploadPhase("error");
      return;
    }
    if (selected.size > MAX_FILE_SIZE_BYTES) {
      setUploadError("File is too large. Maximum size is 25 MB.");
      setUploadPhase("error");
      return;
    }

    setFile(selected);
    setUploadError("");
    setUploadPhase("uploading");

    try {
      const uploadUrl = await generateUploadUrl({});
      const response = await fetch(uploadUrl, {
        method: "POST",
        headers: { "Content-Type": selected.type },
        body: selected,
      });
      if (!response.ok) {
        throw new Error("Upload failed");
      }
      const { storageId: nextStorageId } = (await response.json()) as {
        storageId: Id<"_storage">;
      };
      setStorageId(nextStorageId);
      setUploadPhase("uploaded");
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to upload file";
      setUploadError(message);
      setUploadPhase("error");
      setFile(null);
      setStorageId(null);
    }
  };

  const handleClearFile = () => {
    setFile(null);
    setStorageId(null);
    setUploadPhase("idle");
    setUploadError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!appointmentDate) {
      toast.error("Appointment date is required.");
      return;
    }
    if (!appointmentType) {
      toast.error("Appointment type is required.");
      return;
    }

    try {
      await convertJob({
        jobId,
        appointmentDate,
        appointmentType,
        notes: notes.trim() || undefined,
        targetStatus,
        // NOA is optional — only sent when a file has finished uploading.
        ...(storageId && file
          ? {
              noticeOfAppointmentStorageId: storageId,
              noticeOfAppointmentFileName: file.name,
              noticeOfAppointmentFileType: file.type,
              noticeOfAppointmentFileSize: file.size,
            }
          : {}),
      });

      // Run NOA extraction to populate court/bankruptcy day-one fields.
      // Non-fatal: the conversion already succeeded, so a soft warning
      // is surfaced if extraction fails (mirrors the NOA panel flow).
      if (storageId && file) {
        try {
          const extracted = await extractNoaFields({
            storageId,
            fileName: file.name,
            fileType: file.type,
            fileSize: file.size,
            jobId,
          });
          const jobPatch: Record<string, unknown> & { jobId: Id<"jobs"> } = {
            jobId,
          };
          if (extracted.appointmentType)
            jobPatch.appointmentType = extracted.appointmentType;
          if (extracted.appointmentDate)
            jobPatch.appointmentDate = extracted.appointmentDate;
          if (extracted.notesBlock) jobPatch.notes = extracted.notesBlock;
          if (extracted.courtName) jobPatch.courtName = extracted.courtName;
          if (extracted.courtState) jobPatch.courtState = extracted.courtState;
          if (extracted.courtNumber)
            jobPatch.courtNumber = extracted.courtNumber;
          if (extracted.sequestrationNumber)
            jobPatch.sequestrationNumber = extracted.sequestrationNumber;
          if (extracted.proceedingMatterNumber)
            jobPatch.proceedingMatterNumber = extracted.proceedingMatterNumber;
          if (extracted.proceedingYear)
            jobPatch.proceedingYear = extracted.proceedingYear;
          if (extracted.courtOrderDate)
            jobPatch.courtOrderDate = extracted.courtOrderDate;
          if (extracted.windingUpApplicationDate)
            jobPatch.windingUpApplicationDate =
              extracted.windingUpApplicationDate;
          if (extracted.administrationStartDate)
            jobPatch.administrationStartDate =
              extracted.administrationStartDate;
          if (extracted.plaintiffName)
            jobPatch.plaintiffName = extracted.plaintiffName;
          if (extracted.defendantName)
            jobPatch.defendantName = extracted.defendantName;
          if (extracted.bankruptName)
            jobPatch.bankruptName = extracted.bankruptName;
          if (extracted.administrationNumber)
            jobPatch.administrationNumber = extracted.administrationNumber;
          if (extracted.petitioningCreditorCosts)
            jobPatch.petitioningCreditorCosts =
              extracted.petitioningCreditorCosts;
          if (extracted.actOfBankruptcyDate)
            jobPatch.actOfBankruptcyDate = extracted.actOfBankruptcyDate;
          if (Object.keys(jobPatch).length > 1) {
            await updateJob(jobPatch as Parameters<typeof updateJob>[0]);
          }
        } catch (extractErr) {
          const msg =
            extractErr instanceof Error
              ? extractErr.message
              : String(extractErr);
          toast.warning(`Job converted, but field extraction failed: ${msg}`);
        }
      }

      toast.success("Job converted to appointment");
      onOpenChange(false);
      onConverted?.();
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to convert job";
      toast.error(message);
    }
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const dropped = e.dataTransfer.files?.[0];
    if (dropped) handleFileSelected(dropped);
  };

  const isBusy = uploadPhase === "uploading" || isConverting;
  // The NOA is optional: the user can convert with no file (phase "idle")
  // or with one that finished uploading. We only block while an upload is
  // in flight or sitting in an error state the user hasn't cleared.
  const canSubmit =
    !isBusy &&
    Boolean(appointmentDate) &&
    Boolean(appointmentType) &&
    (uploadPhase === "idle" || uploadPhase === "uploaded");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Convert to Appointment</DialogTitle>
          <DialogDescription>
            Finalise the appointment details to unlock the post-appointment
            workflow. Optionally attach the Notice of Appointment now — if you
            do, it runs through the standard document pipeline; you can also
            upload it later.
          </DialogDescription>
        </DialogHeader>

        <form
          id="convertPreAppointmentForm"
          onSubmit={handleSubmit}
          className="flex flex-col gap-5"
        >
          {/* NOA upload — optional; the job can convert without it. */}
          <div className="flex flex-col gap-2">
            <Label>
              Notice of Appointment{" "}
              <span className="font-normal text-muted-foreground">
                (optional)
              </span>
            </Label>
            <div
              onDragOver={(e) => e.preventDefault()}
              onDrop={onDrop}
              className={cn(
                "flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed bg-muted/20 p-6 text-center transition-colors",
                uploadPhase === "error" &&
                  "border-destructive/50 bg-destructive/5",
              )}
            >
              {file ? (
                <div className="flex w-full flex-col items-center gap-2">
                  <div className="flex items-center gap-2 text-sm">
                    <FileText className="h-4 w-4 text-primary" />
                    <span className="font-medium">{file.name}</span>
                    <button
                      type="button"
                      onClick={handleClearFile}
                      className="rounded-full p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                      aria-label="Remove file"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  {uploadPhase === "uploading" && (
                    <p className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      Uploading…
                    </p>
                  )}
                  {uploadPhase === "uploaded" && (
                    <p className="flex items-center gap-2 text-xs text-emerald-600">
                      <Check className="h-3 w-3" />
                      Uploaded.
                    </p>
                  )}
                  {uploadPhase === "error" && (
                    <p className="text-xs text-destructive">
                      {uploadError || "Upload failed."}
                    </p>
                  )}
                </div>
              ) : (
                <>
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
                    <Upload className="h-4 w-4" />
                  </div>
                  <div className="flex flex-col gap-1">
                    <p className="text-sm font-medium">
                      Drop the Notice of Appointment here, or
                    </p>
                    <p className="text-xs text-muted-foreground">
                      PDF or .docx, up to 25 MB
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Choose file
                  </Button>
                  <p className="text-xs text-muted-foreground">
                    Optional — you can convert now and upload it later.
                  </p>
                  {uploadError && (
                    <p className="text-xs text-destructive">{uploadError}</p>
                  )}
                </>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,application/pdf,.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                className="hidden"
                onChange={(e) => {
                  const selected = e.target.files?.[0];
                  if (selected) void handleFileSelected(selected);
                }}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <Label>Appointment Type *</Label>
              <Select
                value={appointmentType || undefined}
                onValueChange={(v) => setAppointmentType(v as AppointmentType)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {APPOINTMENT_TYPE_GROUPS.map((group) => (
                    <SelectGroup key={group.label}>
                      <SelectLabel>{group.label}</SelectLabel>
                      {group.values.map((value) => (
                        <SelectItem key={value} value={value}>
                          {APPOINTMENT_TYPE_LABELS[value] ?? value}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-2">
              <Label>Appointment Date *</Label>
              <DatePicker
                value={appointmentDate}
                onChange={setAppointmentDate}
                placeholder="Select date"
              />
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <Label>Move To</Label>
            <Select
              value={targetStatus}
              onValueChange={(v) => setTargetStatus(v as "day_one" | "active")}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="day_one">
                  Day One — start standard onboarding workflow
                </SelectItem>
                <SelectItem value="active">Active — skip Day One</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="convertNotes">Notes</Label>
            <Textarea
              id="convertNotes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional context to record alongside the appointment"
              className="min-h-[80px]"
            />
          </div>
        </form>

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={isConverting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="convertPreAppointmentForm"
            disabled={!canSubmit}
          >
            {isConverting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Converting…
              </>
            ) : (
              "Convert"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
